import classNames from 'classnames';
import { useState } from 'react';
import React from 'react';
import { FieldValues, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Loading, Typography } from '@/shared/components/atoms';
import ScrollableWithShadow from '@/shared/components/atoms/ScrollableWithShadow';
import VerticalSteps from '@/shared/components/atoms/VerticalSteps';
import { IMultiStepFormProps } from '@/shared/components/molecules/MultiStepForm/IMultiStepForm';

import ArrowUp from '~/images/shared/arrows/arrow-up.svg';
import Chevron from '~/images/shared/arrows/arrow-up-line.svg';
import Check from '~/images/shared/CheckWithoudBg.svg';

/**
 * A multi-step form component with step navigation and dynamic content rendering and form validation.
 * @template T The type of form data managed by react-hook-form.
 * @param steps Array of form steps. Each step contains:
 *   - `title`: The title of the step.
 *   - `childrenTitle`: An alternative title displayed in the step's content area.
 *   - `render`: A function that renders the content for the step. Receives `step` (current step index) and `setStep` (state setter for navigating steps).
 *   - `fields` (FieldPath<T>[]): An array of field paths (keys of the form data object) used for validation at this step.
 * @param formMethods React Hook Form methods.
 * @param submitButtonLabel The label for the submit button displayed in the review step.
 * @param onSubmit form submission handler.
 * @param className `<form>` element className
 * @param px `<form>` element px
 * @param py `<form>` element py
 */
const MultiStepForm = <T,>({
  steps,
  formMethods,
  submitButtonLabel,
  onSubmit,
  isLoading,
  isMutating,
  className,
  px,
  py,
  showVerticalSteps = true,
}: IMultiStepFormProps<T & FieldValues>) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [areStepsExpanded, setAreStepsExpanded] = useState(false);
  const currStep = steps[step];
  const isReview = step === steps.length - 1;
  const isLastStep = step === steps.length - 2;
  const isSubmitting = isReview && isMutating;
  const isNextDisabled =
    isSubmitting ||
    isLoading ||
    // use getFieldState to access  of nested properties
    currStep.fields.some((field) => !!formMethods.getFieldState(field).error);

  const handleNext = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    // prevent submission
    e.preventDefault();
    const isValid = await formMethods.trigger(currStep.fields);
    if (isValid) setStep((prev) => prev + 1);
  };

  const verticalSteps = (
    <VerticalSteps
      steps={steps.map((formStep, idx) => ({
        title: formStep.title,
        clickable: step > idx,
        onClick: (index) => setStep(index),
      }))}
      stepIndex={step}
    />
  );

  return (
    <form
      className={classNames(
        'flex h-full w-full flex-col tablet:flex-row',
        className,
        px ?? 'desktop:px-64',
        py ?? 'tablet:pt-24 desktop:pt-32'
      )}
      onSubmit={formMethods.handleSubmit(onSubmit)}
    >
      <FormProvider {...formMethods}>
        {showVerticalSteps && (
          <div className='hidden tablet:block'>{verticalSteps}</div>
        )}
        <div className='flex flex-grow justify-center'>
          <div className='flex w-full flex-col justify-between gap-56 tablet:w-[80%] desktop:w-1/2'>
            <div>
              {/* Step header */}
              <div className='border-b-xs border-b-neutralVariant-90 py-8 tablet:border-0 tablet:py-0'>
                <div className='flex items-center justify-between'>
                  <div>
                    <Typography weight='medium' className='text-surface-10'>
                      {`${t('step')} ${t('step-counter', {
                        step: step + 1,
                        totalSteps: steps.length,
                      })}`}
                    </Typography>
                    <Typography weight='semiBold' variant='h1'>
                      {currStep.childrenTitle || currStep.title}
                    </Typography>
                  </div>
                  <div
                    onClick={() => setAreStepsExpanded((state) => !state)}
                    className={classNames(
                      'flex h-32 w-32 cursor-pointer items-center justify-center rounded-lg text-primary-default tablet:hidden',
                      areStepsExpanded
                        ? 'bg-primary-50'
                        : 'border-xs border-outline-default'
                    )}
                  >
                    <span
                      className={classNames(!areStepsExpanded && 'rotate-180')}
                    >
                      <Chevron width='9' height='5' />
                    </span>
                  </div>
                </div>
                {areStepsExpanded && (
                  <div className='my-16 tablet:hidden'>{verticalSteps}</div>
                )}
              </div>
              {/* Step Content */}
              <ScrollableWithShadow className='mt-40'>
                {isLoading ? <Loading /> : currStep.render({ step, setStep })}
              </ScrollableWithShadow>
            </div>
            {/* actions */}
            <div className='flex gap-16'>
              {step > 0 && (
                <Button
                  variant='text'
                  size='none'
                  className={classNames(
                    'flex h-56 w-56 items-center justify-center !rounded-xl border-xs border-outline-default tablet:!w-full'
                  )}
                  onClick={() => setStep((prev) => prev - 1)}
                >
                  <>
                    <span className='text-primary-default ltr:-rotate-90 rtl:rotate-90 tablet:hidden'>
                      <ArrowUp width='20' height='14' />
                    </span>
                    <span className='hidden tablet:inline-block'>
                      {`${t('back')}: ${t(steps[step - 1].title)}`}
                    </span>
                  </>
                </Button>
              )}
              <Button
                type={isReview ? 'submit' : 'button'}
                size='full'
                className='flex h-56 items-center justify-center gap-8 !rounded-xl'
                onClick={!isReview ? handleNext : undefined}
                disabled={isNextDisabled}
                loading={isSubmitting}
              >
                {isReview ? (
                  <>
                    {submitButtonLabel}
                    <Check />
                  </>
                ) : (
                  <>
                    {`${t('next')}: ${t(steps[step + 1].title)}`}
                    {isLastStep && (
                      <span className='ltr:rotate-90 rtl:-rotate-90'>
                        <ArrowUp />
                      </span>
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  );
};

export default MultiStepForm;
